import React, { useEffect, useState, useCallback, useRef } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import LineItemRow from "@/components/order/LineItemRow";
import ReasonDialog from "@/components/order/ReasonDialog";
import RefreshControl from "@/components/RefreshControl";
import useAutoRefresh from "@/lib/useAutoRefresh";
import useBase44Sync from "@/lib/useBase44Sync";
import OrderMetaCard from "@/components/order/OrderMetaCard";
import CancelledBanner from "@/components/order/CancelledBanner";
import OrderPaintChip from "@/components/order/OrderPaintChip";
import SignatureViewer from "@/components/order/SignatureViewer";
import ModificationHistory from "@/components/order/ModificationHistory";
import RevisionBadge from "@/components/order/RevisionBadge";
import { orderProgressPct, isOrderCancelled } from "@/lib/stages";
import { API } from "@/lib/apiClient";
import { ArrowLeft, Printer, CheckCircle, Cube } from "@phosphor-icons/react";

export default function OrderDetail() {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [syncingMissing, setSyncingMissing] = useState(false);
  const [busy, setBusy] = useState(null);
  const [msg, setMsg] = useState(null);
  const [reasonItem, setReasonItem] = useState(null); // {item, action}
  const [reason, setReason] = useState("");
  // Track whether we've already attempted a Base44 sync to find this id, so
  // auto-refresh loops don't repeatedly hit the sync endpoint.
  const syncTriedRef = useRef(null);

  const { runSync, syncing, canSync } = useBase44Sync();

  // Run the parallel detail + list fetch once. Returns the merged order or
  // null if the detail endpoint 404s.
  const tryFetchOnce = useCallback(async () => {
    let detail = null;
    let listData = null;
    try {
      // Fetch active + completed list in parallel — the backend defaults
      // `/api/orders` to active-only, so without `status=completed` a signed
      // order's summary fields would never reach the merge below.
      const [detailRes, activeListRes, completedListRes, cancelledListRes] = await Promise.all([
        api.get(`/orders/${id}`).catch((e) => {
          if (e?.response?.status === 404) return null;
          throw e;
        }),
        api.get(`/orders`, { params: { status: "active" } }).catch(() => null),
        api.get(`/orders`, { params: { status: "completed" } }).catch(() => null),
        api.get(`/orders`, { params: { status: "cancelled" } }).catch(() => null),
      ]);
      detail = detailRes ? detailRes.data : null;
      listData = [
        ...((activeListRes && activeListRes.data) || []),
        ...((completedListRes && completedListRes.data) || []),
        ...((cancelledListRes && cancelledListRes.data) || []),
      ];
    } catch (err) {
      console.warn("OrderDetail: fetch failed", err);
      return null;
    }
    if (!detail) return null;
    const summary = (listData || []).find((o) => o && o.id === id);
    return summary ? mergeOrderSummary(detail, summary) : detail;
  }, [id]);

  const fetchOrder = useCallback(async () => {
    try {
      let merged = await tryFetchOnce();
      // If the order isn't in the local DB but the user is admin/manager,
      // try one Base44 sync (per :id) and retry. Foolproofs deep-links to
      // brand-new orders that arrived in Base44 after the last sync.
      if (!merged && canSync && syncTriedRef.current !== id) {
        syncTriedRef.current = id;
        setSyncingMissing(true);
        try {
          await api.post("/base44/sync");
          merged = await tryFetchOnce();
          if (merged) toast.success("Found via Base44 sync");
        } catch (err) {
          console.warn("OrderDetail: Base44 sync failed", err);
        } finally {
          setSyncingMissing(false);
        }
      }
      setOrder(merged);
    } finally { setLoading(false); }
  }, [tryFetchOnce, canSync, id]);

  const { refresh, refreshing, lastUpdatedAt } = useAutoRefresh(fetchOrder, { skipInitial: true });

  // Initial + on :id change refetch (and reset the sync-tried marker).
  useEffect(() => {
    syncTriedRef.current = null;
    setLoading(true);
    refresh();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const advanceItem = useCallback(async (item) => {
    setBusy(item.id);
    setMsg(null);
    try {
      // All advance-step clicks on web are human button-pushes (no USB/BT
      // scanner integration yet). Tag them "manual" so admins can tell UI
      // clicks apart from real barcode scans when auditing an item's history.
      await api.post("/items/scan", { item_id: item.id, method: "manual" });
      setMsg({ type: "success", text: `${item.sku} advanced` });
      await fetchOrder();
    } catch (err) {
      setMsg({ type: "error", text: err?.response?.data?.detail || "Scan failed" });
    } finally {
      setBusy(null);
    }
  }, [fetchOrder]);

  const submitReason = useCallback(async () => {
    if (!reasonItem || !reason.trim()) return;
    setBusy(reasonItem.item.id);
    try {
      const path = reasonItem.action === "qa-fail"
        ? `/items/${reasonItem.item.id}/qa-fail`
        : `/items/${reasonItem.item.id}/cancel`;
      await api.post(path, { reason: reason.trim() });
      setMsg({ type: "success", text: `${reasonItem.item.sku} — ${reasonItem.action}` });
      setReasonItem(null);
      setReason("");
      await fetchOrder();
    } catch (err) {
      setMsg({ type: "error", text: err?.response?.data?.detail || "Action failed" });
    } finally {
      setBusy(null);
    }
  }, [reasonItem, reason, fetchOrder]);

  const uncancelItem = useCallback(async (item) => {
    setBusy(item.id);
    try {
      await api.post(`/items/${item.id}/uncancel`);
      setMsg({ type: "success", text: `${item.sku} restored` });
      await fetchOrder();
    } catch (err) {
      setMsg({ type: "error", text: err?.response?.data?.detail || "Failed" });
    } finally {
      setBusy(null);
    }
  }, [fetchOrder]);

  const openReceivingQR = useCallback(async () => {
    if (!order?.id) return;
    setBusy("qr");
    try {
      const res = await api.get(`/orders/${order.id}/receiving-qr`, { responseType: "text" });
      const html = res.data;
      const blob = new Blob([html], { type: "text/html" });
      const url = URL.createObjectURL(blob);
      const win = window.open(url, "_blank", "noopener,noreferrer");
      if (!win) {
        setMsg({ type: "error", text: "Allow pop-ups to open the receiving QR" });
        URL.revokeObjectURL(url);
        return;
      }
      // Revoke after the new tab has had time to load
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (err) {
      setMsg({ type: "error", text: err?.response?.data?.detail || "Failed to load receiving QR" });
    } finally {
      setBusy(null);
    }
  }, [order?.id]);

  if (loading) {
    return (
      <div className="text-zinc-500" data-testid="order-detail-loading">
        {syncingMissing ? (
          <>
            Syncing from Base44 to find this order…
            <div className="text-xs text-zinc-400 mt-1">This can take a few seconds.</div>
          </>
        ) : "Loading order…"}
      </div>
    );
  }
  if (!order) {
    return (
      <div className="space-y-3" data-testid="order-detail-not-found">
        <h1 className="font-heading font-black text-2xl">Order not found</h1>
        <p className="text-sm text-zinc-600 dark:text-zinc-300">
          {canSync
            ? "We tried syncing from Base44 but couldn't find this order. The link may be stale, or the order may exist in your other view mode (Live / Test)."
            : "This order isn't synced to the local database yet. Ask an admin to run a Base44 sync, or check that your view mode (Live / Test) matches."}
        </p>
        <Link to="/orders" className="text-[#C8102E] font-semibold hover:underline">
          Go to orders →
        </Link>
      </div>
    );
  }

  const items = order.items || [];
  const progress = orderProgressPct(order);
  const cancelled = isOrderCancelled(order);

  return (
    <div className="space-y-6" data-testid="order-detail-page">
      <Link to="/orders" className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 font-semibold" data-testid="back-to-orders">
        <ArrowLeft size={14} weight="bold" /> Back to orders
      </Link>

      {cancelled && <CancelledBanner order={order} />}

      <ModificationHistory order={order} onAcknowledge={refresh} />

      <OrderHeader order={order} busy={busy} onOpenReceivingQR={openReceivingQR} cancelled={cancelled} refreshControl={
        <RefreshControl
          onRefresh={refresh}
          refreshing={refreshing}
          lastUpdatedAt={lastUpdatedAt}
          onSync={canSync ? () => runSync(refresh) : null}
          syncing={syncing}
          testId="order-detail-refresh"
        />
      } />
      <OrderMetaCard order={order} />
      {!cancelled && <ProgressCard order={order} progress={progress} />}
      <ActionFeedback msg={msg} />

      <section>
        <h2 className="font-heading font-bold text-xl mb-3">Line items · {items.length}</h2>
        <Card className="border-zinc-200 overflow-hidden">
          <div className="divide-y divide-zinc-200">
            {items.map((item) => (
              <LineItemRow
                key={item.id}
                item={item}
                order={order}
                busy={busy}
                onAdvance={cancelled ? null : advanceItem}
                onAskReason={cancelled ? null : (payload) => { setReasonItem(payload); setReason(""); }}
                onUncancel={cancelled ? null : uncancelItem}
              />
            ))}
          </div>
        </Card>
      </section>

      <ReasonDialog
        open={!!reasonItem}
        action={reasonItem?.action}
        item={reasonItem?.item}
        reason={reason}
        setReason={setReason}
        busy={!!busy}
        onSubmit={submitReason}
        onClose={() => setReasonItem(null)}
      />
    </div>
  );
}

function OrderHeader({ order, busy, onOpenReceivingQR, cancelled, refreshControl }) {
  return (
    <div className="flex flex-wrap items-start gap-6 justify-between">
      <div>
        <div className="text-[10px] uppercase tracking-[0.3em] text-zinc-500 font-semibold">Order</div>
        <div className="flex items-center gap-2 flex-wrap mt-1">
          <h1 className={`font-heading font-black text-3xl sm:text-4xl ${cancelled ? "line-through text-zinc-400" : ""}`} data-testid="order-number-heading">
            {order.order_number}
          </h1>
          <RevisionBadge order={order} size="md" />
        </div>
        <div className="text-zinc-700 text-base">{order.customer_name}</div>
        {order.customer_address && (
          <div className="text-sm text-zinc-500">{order.customer_address}</div>
        )}
        {order.external_id && (
          <div className="text-xs text-zinc-400 font-mono mt-1">Base44: {order.external_id}</div>
        )}
        {Number(order.paint_out_count) > 0 && !cancelled && (
          <div className="mt-2">
            <OrderPaintChip order={order} />
          </div>
        )}
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        {refreshControl}
        {!cancelled && order.has_specialty_items && (
          <Button
            variant="outline"
            onClick={onOpenReceivingQR}
            disabled={busy === "qr"}
            className="h-11 border-purple-300 text-purple-700 hover:bg-purple-50 dark:border-purple-700 dark:text-purple-300"
            data-testid="receiving-qr-button"
          >
            <Cube size={18} weight="bold" className="mr-1.5" />
            {busy === "qr" ? "Loading…" : "Receiving QR"}
          </Button>
        )}
        {!cancelled && (
          <Link to={`/labels/${order.id}`}>
            <Button className="bg-[#C8102E] hover:bg-[#A00D25] h-11" data-testid="print-labels-button">
              <Printer size={18} weight="bold" className="mr-1.5" /> Print Labels
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
}

function ProgressCard({ order, progress }) {
  return (
    <Card className="p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 font-semibold">
          Overall progress · {order.completed_count}/{order.item_count} items
        </div>
        <span className="font-mono font-semibold text-sm">{progress}%</span>
      </div>
      <div className="h-2.5 rounded-full bg-zinc-100 overflow-hidden">
        <div className="h-full bg-[#C8102E] transition-all" style={{ width: `${progress}%` }} />
      </div>
      <SignatureViewer order={order} />
    </Card>
  );
}

function ActionFeedback({ msg }) {
  if (!msg) return null;
  return (
    <div
      data-testid="action-feedback"
      className={`px-4 py-3 rounded-md text-sm font-semibold ${
        msg.type === "success"
          ? "bg-emerald-50 text-emerald-800 border border-emerald-200"
          : "bg-red-50 text-red-700 border border-red-200"
      }`}
    >
      {msg.text}
    </div>
  );
}

// Fields the detail endpoint sometimes drops but the list endpoint serializes
// correctly. We overlay them only when the detail value is null/undefined so
// the user never loses any data the backend actually returned on detail.
const SUMMARY_FALLBACK_FIELDS = [
  "has_specialty_items",
  "specialty_count",
  "specialty_received_count",
  "specialty_payment_status",
  "delivery_date",
  "fulfillment_type",
  "customer_email",
  "po_number",
  "store",
  "notes",
  "completed_count",
  "item_count",
  "status",
  "signed_at",
  "signed_by",
  "external_status",
  "payment_status",
  "shipping_address",
  "shipping_street",
  "shipping_city",
  "shipping_state",
  "shipping_zip",
  "shipping_country",
  "unit_number",
  "signature_svg",
];

function mergeOrderSummary(detail, summary) {
  if (!detail) return summary || null;
  if (!summary) return detail;
  const merged = { ...detail };
  for (const key of SUMMARY_FALLBACK_FIELDS) {
    if (merged[key] == null && summary[key] != null) {
      merged[key] = summary[key];
    }
  }
  return merged;
}
